/**
 * Utility functions for CampaignManagement components
 * See docs/CAMPAIGN_MANAGEMENT_CARD_REDESIGN.md for full design spec
 */

import type { Campaign, AdGroup, AssetGroup, Ad } from '@/types/campaign';
import type { EntityTab, EntityType, EntitySummary, CampaignLevelSummary } from './types';

/**
 * Format a number as currency (USD)
 * Returns '$0.00' for undefined/null/NaN (use for forms/management)
 */
export function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0.00';
  return `$${value.toFixed(2)}`;
}

/**
 * Format a number as currency for display
 * Returns '-' for undefined/null/NaN (use for metrics display)
 */
export function formatCurrencyDisplay(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  return `$${value.toFixed(2)}`;
}

/**
 * Format a number with commas
 * Returns '0' for undefined/null/NaN (use for forms/management)
 */
export function formatNumber(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '0';
  return value.toLocaleString();
}

/**
 * Format a number for display
 * Returns '-' for undefined/null/NaN (use for metrics display)
 */
export function formatNumberDisplay(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  return value.toLocaleString();
}

/**
 * Format large numbers with K/M suffixes for compact display
 * Returns '-' for undefined/null/NaN
 * Limits decimal places to 2 for consistent display of computed averages
 */
export function formatNumberCompact(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a percentage
 * Returns '0%' for undefined/null/NaN (use for forms/management)
 */
export function formatPercent(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '0%';
  return `${value.toFixed(2)}%`;
}

/**
 * Format a percentage for display
 * Returns '-' for undefined/null/NaN (use for metrics display)
 */
export function formatPercentDisplay(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  return `${value.toFixed(2)}%`;
}

/**
 * Check if ad group has metrics data
 */
export function hasAdGroupMetrics(adGroup: AdGroup): boolean {
  return (
    (adGroup.impressions !== undefined && adGroup.impressions > 0) ||
    (adGroup.clicks !== undefined && adGroup.clicks > 0) ||
    (adGroup.cost !== undefined && adGroup.cost > 0) ||
    (adGroup.conversions !== undefined && adGroup.conversions > 0)
  );
}

/**
 * Get the ID from an entity (ad group or asset group)
 */
export function getEntityId(entity: AdGroup | AssetGroup, entityType: EntityType): string {
  if (entityType === 'asset_group') {
    return (entity as AssetGroup).asset_group_id;
  }
  const adGroup = entity as AdGroup;
  return adGroup.ad_group_id || adGroup.ads_group_id || '';
}

/**
 * Get the name from an entity (ad group or asset group)
 */
export function getEntityName(entity: AdGroup | AssetGroup, entityType: EntityType): string {
  if (entityType === 'asset_group') {
    return (entity as AssetGroup).asset_group_name || 'Unnamed Asset Group';
  }
  const adGroup = entity as AdGroup;
  return adGroup.ad_group_name || adGroup.ads_group_name || 'Unnamed Ad Group';
}

/**
 * Get RSAs from an ad group
 */
export function getRSAsFromAdGroup(adGroup: AdGroup): Ad[] {
  const adsList = adGroup.ads_list || adGroup.ads || [];
  return adsList.filter(ad =>
    ad.ad_type === 'RESPONSIVE_SEARCH_AD' ||
    ad.ad_type?.includes('RESPONSIVE_SEARCH')
  );
}

/**
 * Get RDAs from an ad group
 */
export function getRDAsFromAdGroup(adGroup: AdGroup): Ad[] {
  const adsList = adGroup.ads_list || adGroup.ads || [];
  return adsList.filter(ad =>
    ad.ad_type === 'RESPONSIVE_DISPLAY_AD' ||
    ad.ad_type?.includes('RESPONSIVE_DISPLAY')
  );
}

/**
 * Get Demand Gen ads from an ad group
 */
export function getDemandGenAdsFromAdGroup(adGroup: AdGroup): Ad[] {
  const adsList = adGroup.ads_list || adGroup.ads || [];
  return adsList.filter(ad =>
    ad.ad_type?.includes('DEMAND_GEN') ||
    ad.ad_type === 'DEMAND_GEN_MULTI_ASSET_AD' ||
    ad.ad_type === 'DEMAND_GEN_VIDEO_RESPONSIVE_AD' ||
    ad.ad_type === 'DEMAND_GEN_CAROUSEL_AD'
  );
}

/**
 * Count text assets for an ad group
 */
function countAdGroupTextAssets(adGroup: AdGroup): number {
  // Count assets from ads (headlines + descriptions)
  const adsList = adGroup.ads_list || adGroup.ads || [];
  let count = 0;

  for (const ad of adsList) {
    count += ad.headlines?.length || 0;
    count += ad.descriptions?.length || 0;
  }

  return count;
}

/**
 * Count text assets for an asset group
 */
function countAssetGroupTextAssets(assetGroup: AssetGroup): number {
  const assetList = assetGroup.asset_list || [];
  return assetList.filter(asset => {
    const type = asset.type || asset.asset_type;
    return type === 'headline' || type === 'description' || type === 'long_headline';
  }).length;
}

/**
 * Count image assets for an asset group
 */
function countAssetGroupImageAssets(assetGroup: AssetGroup): number {
  return (
    (assetGroup.marketing_images?.length || 0) +
    (assetGroup.square_marketing_images?.length || 0) +
    (assetGroup.portrait_marketing_images?.length || 0) +
    (assetGroup.logo_images?.length || 0)
  );
}

/**
 * Get summary stats for an entity
 */
export function getEntitySummary(entity: AdGroup | AssetGroup, entityType: EntityType, campaign?: Campaign): EntitySummary {
  if (entityType === 'asset_group') {
    const assetGroup = entity as AssetGroup;
    // For PMax, negative keywords are at campaign level but we show them in asset group summary
    const isPMax = campaign?.campaign_type?.toUpperCase().includes('PERFORMANCE_MAX');
    return {
      keywordCount: 0, // PMax doesn't have keywords at asset group level
      rsaCount: 0,
      rdaCount: 0,
      assetCount: countAssetGroupTextAssets(assetGroup),
      imageAssetCount: countAssetGroupImageAssets(assetGroup),
      negativeKeywordCount: isPMax ? (campaign?.negative_keywords?.length || 0) : 0,
    };
  }

  const adGroup = entity as AdGroup;
  return {
    keywordCount: adGroup.keywords_list?.length || 0,
    rsaCount: getRSAsFromAdGroup(adGroup).length,
    rdaCount: getRDAsFromAdGroup(adGroup).length,
    demandGenAdCount: getDemandGenAdsFromAdGroup(adGroup).length,
    assetCount: countAdGroupTextAssets(adGroup),
    imageAssetCount: 0, // Ad groups don't have direct image assets
    negativeKeywordCount: 0,
  };
}

/**
 * Get summary stats for campaign level assets
 */
export function getCampaignLevelSummary(campaign: Campaign): CampaignLevelSummary {
  return {
    sitelinkCount: campaign.sitelinks?.length || 0,
    calloutCount: campaign.callouts?.length || 0,
    snippetCount: campaign.structured_snippets?.length || 0,
    imageCount: 0, // Campaign-level images not yet tracked
  };
}

/**
 * Format summary stats as display string
 */
export function formatEntitySummary(summary: EntitySummary, entityType: EntityType): string {
  const parts: string[] = [];

  if (summary.keywordCount > 0) {
    parts.push(`${summary.keywordCount} keyword${summary.keywordCount !== 1 ? 's' : ''}`);
  }

  if (summary.assetCount > 0) {
    parts.push(`${summary.assetCount} text asset${summary.assetCount !== 1 ? 's' : ''}`);
  }

  if (summary.rsaCount > 0) {
    parts.push(`${summary.rsaCount} RSA${summary.rsaCount !== 1 ? 's' : ''}`);
  }

  if (summary.rdaCount > 0) {
    parts.push(`${summary.rdaCount} RDA${summary.rdaCount !== 1 ? 's' : ''}`);
  }

  if (summary.demandGenAdCount && summary.demandGenAdCount > 0) {
    parts.push(`${summary.demandGenAdCount} Demand Gen ad${summary.demandGenAdCount !== 1 ? 's' : ''}`);
  }

  if (summary.imageAssetCount > 0) {
    parts.push(`${summary.imageAssetCount} image${summary.imageAssetCount !== 1 ? 's' : ''}`);
  }

  if (summary.negativeKeywordCount > 0) {
    parts.push(`${summary.negativeKeywordCount} Negative Keyword${summary.negativeKeywordCount !== 1 ? 's' : ''}`);
  }

  // If no items, show appropriate message
  if (parts.length === 0) {
    return entityType === 'asset_group' ? 'No assets' : 'No keywords or ads';
  }

  return parts.join('  •  ');
}

/**
 * Format campaign level summary as display string
 */
export function formatCampaignLevelSummary(summary: CampaignLevelSummary): string {
  const parts: string[] = [];

  if (summary.sitelinkCount > 0) {
    parts.push(`${summary.sitelinkCount} sitelink${summary.sitelinkCount !== 1 ? 's' : ''}`);
  }

  if (summary.calloutCount > 0) {
    parts.push(`${summary.calloutCount} callout${summary.calloutCount !== 1 ? 's' : ''}`);
  }

  if (summary.snippetCount > 0) {
    parts.push(`${summary.snippetCount} structured snippet${summary.snippetCount !== 1 ? 's' : ''}`);
  }

  if (summary.imageCount > 0) {
    parts.push(`${summary.imageCount} image${summary.imageCount !== 1 ? 's' : ''}`);
  }

  if (parts.length === 0) {
    return 'No campaign-level assets';
  }

  return parts.join('  •  ');
}

/**
 * Get available tabs for an entity based on campaign type
 */
export function getAvailableTabs(campaignType: string, entityType: EntityType): EntityTab[] {
  const tabs: EntityTab[] = [];
  const normalizedType = campaignType.toUpperCase();

  const isSearch = normalizedType.includes('SEARCH');
  const isDisplay = normalizedType.includes('DISPLAY');
  const isPMax = normalizedType.includes('PERFORMANCE_MAX');
  const isAIMax = normalizedType.includes('AI_MAX');
  const isDemandGen = normalizedType.includes('DEMAND_GEN');

  // Keywords - Search, Display, AI Max (ad groups only)
  // Note: Display campaigns use contextual targeting keywords
  if ((isSearch || isDisplay || isAIMax) && entityType === 'ad_group') {
    tabs.push('keywords');
  }

  // Demand Gen Ads - Demand Gen (ad groups only)
  if (isDemandGen && entityType === 'ad_group') {
    tabs.push('demand-gen-ads');
  }

  // Channels - Demand Gen (ad groups only)
  if (isDemandGen && entityType === 'ad_group') {
    tabs.push('channels');
  }

  // Audiences - Demand Gen (ad groups only) - for audience targeting management
  if (isDemandGen && entityType === 'ad_group') {
    tabs.push('audiences');
  }

  // Assets - all types except Display and Demand Gen (managed in ads)
  if (!isDisplay && !isDemandGen) {
    tabs.push('assets');
  }

  // RSAs - Search, AI Max (ad groups only)
  if ((isSearch || isAIMax) && entityType === 'ad_group') {
    tabs.push('rsas');
  }

  // RDAs - Display (ad groups only)
  if (isDisplay && entityType === 'ad_group') {
    tabs.push('rdas');
  }

  // Negative Keywords - PMax only
  if (isPMax) {
    tabs.push('negative-keywords');
  }

  // Note: Search Terms tab moved to Performance Metrics card

  return tabs;
}

/**
 * Get tab counts for display in tab bar
 */
export function getTabCounts(
  entity: AdGroup | AssetGroup,
  entityType: EntityType,
  campaign: Campaign
): Partial<Record<EntityTab, number>> {
  const counts: Partial<Record<EntityTab, number>> = {};

  if (entityType === 'ad_group') {
    const adGroup = entity as AdGroup;
    counts['keywords'] = adGroup.keywords_list?.length || 0;
    counts['rsas'] = getRSAsFromAdGroup(adGroup).length;
    counts['rdas'] = getRDAsFromAdGroup(adGroup).length;
    counts['demand-gen-ads'] = getDemandGenAdsFromAdGroup(adGroup).length;
    counts['assets'] = countAdGroupTextAssets(adGroup);
  } else {
    const assetGroup = entity as AssetGroup;
    counts['assets'] = countAssetGroupTextAssets(assetGroup) + countAssetGroupImageAssets(assetGroup);
  }

  // Negative keywords are at campaign level for PMax
  if (campaign.campaign_type?.includes('PERFORMANCE_MAX')) {
    counts['negative-keywords'] = campaign.negative_keywords?.length || 0;
  }

  return counts;
}

/**
 * Check if campaign has any entities (ad groups or asset groups)
 */
export function hasEntities(campaign: Campaign): boolean {
  const campaignType = campaign.campaign_type?.toUpperCase() || '';
  const isPMax = campaignType.includes('PERFORMANCE_MAX');

  if (isPMax) {
    return (campaign.asset_group_list?.length || 0) > 0;
  }

  return (campaign.ads_group_list?.length || 0) > 0;
}

/**
 * Get entity type based on campaign type
 */
export function getEntityType(campaignType: string): EntityType {
  const normalizedType = campaignType.toUpperCase();
  if (normalizedType.includes('PERFORMANCE_MAX')) {
    return 'asset_group';
  }
  return 'ad_group';
}

/**
 * Get entities from campaign based on campaign type
 */
export function getEntities(campaign: Campaign): Array<AdGroup | AssetGroup> {
  const entityType = getEntityType(campaign.campaign_type || '');
  if (entityType === 'asset_group') {
    return campaign.asset_group_list || [];
  }
  return campaign.ads_group_list || [];
}

/**
 * Get the label for the entity list section
 */
export function getEntityListLabel(campaignType: string, count: number): string {
  const entityType = getEntityType(campaignType);
  if (entityType === 'asset_group') {
    return `Asset Groups (${count})`;
  }
  return `Ad Groups (${count})`;
}

/**
 * Check if campaign level assets section should be shown
 */
export function shouldShowCampaignLevelAssets(campaign: Campaign): boolean {
  const summary = getCampaignLevelSummary(campaign);
  const campaignType = campaign.campaign_type || '';

  // Show if there are any campaign-level assets or if the campaign type supports them
  return (
    summary.sitelinkCount > 0 ||
    summary.calloutCount > 0 ||
    summary.snippetCount > 0 ||
    summary.imageCount > 0 ||
    // Always show for campaign types that support campaign-level assets
    campaignType.includes('SEARCH') ||
    campaignType.includes('PERFORMANCE_MAX')
  );
}
